import { useEffect, useRef, useState, type ReactNode } from 'react'

import { cn } from '../../lib/cn'
import s from './DocumentPreview.module.css'
import { renderPdfPages } from './render-pdf-pages'

/**
 * DocumentPreview — le cadre qui montre un PDF **déjà chargé dans le navigateur**, à côté de ce
 * qu'on en a tiré. Report de la zone d'aperçu que le prototype back-office pose sous la carte
 * « PDF source » (`mocks/ghp-bo/screens.js`, écran de correction d'un PDF lu).
 *
 * **Les pages sont dessinées par pdf.js**, pas par la visionneuse du navigateur (#356). Celle-ci
 * diffère d'un navigateur à l'autre et ne se règle pas : Chrome pose sa barre d'outils et ses
 * vignettes dans le cadre (la moitié de la carte à 560 px), Safari ne recale pas la page quand le
 * cadre change de largeur — elle reste coupée, sous un ascenseur horizontal. pdf.js rend les
 * mêmes pages partout, à la largeur du cadre, sans bouton étranger à l'écran.
 *
 * ⚠️ **Le composant ne fabrique ni ne libère l'URL.** `src` est un `blob:` dont la propriété
 * appartient à l'écran : lui seul sait quand le document cesse d'être à l'écran, et une URL
 * révoquée par le cadre au démontage casserait un appelant qui la réutilise ailleurs.
 */
export interface DocumentPreviewProps {
  /** L'URL du PDF à afficher — typiquement un `blob:` fabriqué par l'écran. */
  src: string
  /**
   * Le nom accessible du cadre. Obligatoire : des pages dessinées ne disent pas ce qu'elles
   * montrent, alors que c'est la moitié de l'écran.
   */
  label: string
  /** Ce qui s'affiche à la place quand le document ne se rend pas — fichier illisible. */
  children?: ReactNode
  /**
   * Remplit la hauteur de son parent au lieu des 520 px de l'écran de correction — le corps de la
   * fenêtre « Fiche PDF », qui a sa propre hauteur. Le parent doit en avoir une.
   */
  fill?: boolean
  /**
   * L'échelle des pages, 1 étant la largeur du cadre (#585). Les pages se redessinent à chaque
   * changement : étirées au CSS, elles seraient floues.
   */
  zoom?: number
  className?: string
}

export function DocumentPreview({
  src,
  label,
  children,
  fill,
  zoom = 1,
  className,
}: DocumentPreviewProps) {
  const pages = useRef<HTMLDivElement>(null)
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const failed = failedSrc === src

  useEffect(() => {
    const host = pages.current
    if (host === null) return

    const rendering = new AbortController()
    renderPdfPages(src, host, rendering.signal, zoom).catch(() => {
      if (!rendering.signal.aborted) setFailedSrc(src)
    })

    return () => {
      rendering.abort()
    }
  }, [src, zoom])

  return (
    <div
      className={cn(s.frame, fill === true && s.fill, className)}
      role="figure"
      aria-label={label}
    >
      {failed ? children : <div ref={pages} className={s.pages} />}
    </div>
  )
}
